// dev-portal/src/pages/auth/CompleteProfile.jsx

import { useState } from 'react';
import { api } from '../../api/client';
import { USER_ROLES } from '../../constants/userRoles';

export default function CompleteProfile() {
    const [selectedRole, setSelectedRole] = useState('');
    const [form, setForm] = useState({
        full_name: '',
        company_name: '',
        website: '',
    });
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const roles = USER_ROLES;

    // Check if selected role is developer/software company
    const isDeveloperRole = selectedRole === 'developer_software_company';

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Handle role selection change
    const handleRoleChange = (roleId) => {
        setSelectedRole(roleId);
        
        // If switching away from developer role, clear company fields
        if (roleId !== 'developer_software_company') {
            setForm(prev => ({
                ...prev,
                company_name: '',
                website: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.full_name.trim() || !selectedRole) {
            setError('Please fill in all required fields');
            return;
        }

        // Only validate company fields for developer role
        if (isDeveloperRole && !form.company_name.trim()) {
            setError('Company name is required for developers');
            return;
        }

        setSubmitting(true);

        try {
            // Prepare submission data
            const submitData = {
                full_name: form.full_name,
                role: selectedRole
            };

            // Only include company fields if developer role
            if (isDeveloperRole) {
                submitData.company_name = form.company_name;
                submitData.website = form.website || '';
            }
            // For non-developer roles, company_name can be blank (not sent)

            const response = await api.post('/api/complete-profile', submitData);

            // Update token with new user info that includes role_selected=true
            if (response.data.access_token) {
                localStorage.setItem('token', response.data.access_token);
                localStorage.setItem('role', response.data.user.role);
            }

            // Show success message briefly then redirect
            setTimeout(() => {
                window.location.replace('/dashboard');
            }, 500);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to complete profile');
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 px-4 py-8">
            <div className="bg-white shadow-2xl rounded-2xl px-8 pt-8 pb-10 w-full max-w-md">
                <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        Welcome! Please Enter Your Name and Select Your Role
                    </h2>
                    <p className="text-gray-600 text-sm">
                        Complete your profile to get started
                    </p>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Full Name */}
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">
                            Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            type="text"
                            name="full_name"
                            placeholder="Enter your full name"
                            value={form.full_name}
                            onChange={handleChange}
                            maxLength={30}
                            required
                            disabled={submitting}
                        />
                        <p className="text-xs text-gray-500 mt-1">{form.full_name.length}/30 characters</p>
                    </div>

                        {/* Company Name - Only show for Developer role */}
                        {isDeveloperRole && (
                            <div className="border-t pt-4 space-y-4">
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-2">
                                        Company Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        type="text"
                                        name="company_name"
                                        placeholder="Enter your company name"
                                        value={form.company_name}
                                        onChange={handleChange}
                                        required
                                        disabled={submitting}
                                    />
                                </div>

                                {/* Website (Optional) */}
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-2">
                                        Website (optional)
                                    </label>
                                    <input
                                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        type="text"
                                        name="website"
                                        placeholder="https://yourcompany.com"
                                        value={form.website}
                                        onChange={handleChange}
                                        disabled={submitting}
                                    />
                                </div>
                            </div>
                        )}
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-3">
                            Select Your Role <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-1 gap-3">
                            {roles.map((role) => (
                                <label
                                    key={role.id}
                                    className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition ${selectedRole === role.id
                                        ? 'border-blue-600 bg-blue-50'
                                        : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                                        } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name="role"
                                        value={role.id}
                                        checked={selectedRole === role.id}
                                        onChange={(e) => handleRoleChange(e.target.value)}
                                        className="w-5 h-5 text-blue-600"
                                        disabled={submitting}
                                    />
                                    <span className="text-base font-medium text-gray-800">{role.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold mt-6 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Processing...
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Continue to Dashboard
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}